import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { Fullscreen, FullscreenExit, Refresh } from '@mui/icons-material';
import { AnimatePresence, motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { KpiItem, KpiStrip, PageHeader, EmptyState } from '../../../components/ui/PageKit';
import { fmtPct } from '../shared';
import { plantLayoutForTenant } from './layouts';
import { usePlantData, zoneStatus, ZoneStatus, findMachine, shiftsToday } from './data';
import PlantMap from './PlantMap';
import ScalePanel from './ScalePanel';
import {
  DeliveryPanel, GrindingPanel, MachinePanel, MaterialsPanel, MixPanel, MoldsPanel, OfficePanel, PanelProps, ProductsPanel,
  SoonPanel, SummaryPanel, WipPanel,
} from './panels';
import { AnimatedNumber, MUTED, PulseDot, SURFACE } from './ui';

const PANELS: Record<string, React.FC<PanelProps>> = {
  machine: MachinePanel,
  products: ProductsPanel,
  materials: MaterialsPanel,
  inputs: MaterialsPanel,
  mix: MixPanel,
  grinding: GrindingPanel,
  molds: MoldsPanel,
  wip: WipPanel,
  delivery: DeliveryPanel,
  office: OfficePanel,
  soon: SoonPanel,
};

/**
 * Planta: el plano de la fábrica con cada sector tocable. Al tocar uno se abre su panel (stock, máquina,
 * matrices, balanza…) y arriba quedan los indicadores del día. Todo se refresca solo cada minuto, así que
 * sirve también como pantalla en vivo en la planta (botón Pantalla completa).
 */
const FactoryPlantPage: React.FC = () => {
  const layout = useMemo(() => plantLayoutForTenant(), []);
  const { data, loading, updatedAt, failed, reload } = usePlantData(60_000);
  const [params, setParams] = useSearchParams();
  const selected = params.get('sector');
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [full, setFull] = useState(false);

  const select = useCallback((id: string | null) => {
    setParams(p => {
      const n = new URLSearchParams(p);
      if (id) n.set('sector', id); else n.delete('sector');
      return n;
    }, { replace: true });
    if (id && window.innerWidth < 1200) setTimeout(() => panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  }, [setParams]);

  useEffect(() => {
    const onChange = () => setFull(document.fullscreenElement === rootRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFull = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
    else rootRef.current?.requestFullscreen?.().catch(() => undefined);
  };

  const statuses = useMemo(() => {
    const out: Record<string, ZoneStatus> = {};
    layout?.zones.forEach(z => { out[z.id] = zoneStatus(z, data); });
    return out;
  }, [layout, data]);

  if (!layout) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, pt: 3 }}>
        <PageHeader title="Planta" />
        <Paper variant="outlined"><EmptyState title="Tu planta todavía no tiene plano" hint="El plano se arma a partir del relevamiento de la fábrica. Pedilo y aparece acá con cada sector tocable." /></Paper>
      </Box>
    );
  }

  const zone = layout.zones.find(z => z.id === selected) ?? null;
  const k = data.kpisToday;
  const machineZones = layout.zones.filter(z => z.kind === 'machine');
  const machinesRunning = machineZones.filter(z => {
    const m = findMachine(z, data.machines);
    return m && shiftsToday(data, m.id).length > 0;
  }).length;
  const red = data.stock.products.filter(p => p.level === 'rojo').length;
  const yellow = data.stock.products.filter(p => p.level === 'amarillo').length;
  const overdue = data.overview.reduce((a, o) => a + o.overdueCount, 0);
  const soon = data.overview.reduce((a, o) => a + o.soonCount, 0);
  const mpKg = data.materials.filter(m => m.origin).reduce((a, m) => a + m.currentStock, 0);
  const virgin = data.materials.filter(m => m.origin === 'Virgen').reduce((a, m) => a + m.currentStock, 0);

  const mapHeight = full ? 'calc(100vh - 190px)' : 'max(560px, calc(100vh - 300px))';
  const Panel = zone ? (zone.kind === 'scale' ? null : PANELS[zone.kind]) : null;

  return (
    <Box ref={rootRef} sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: full ? 2 : 3 }, pb: 4, bgcolor: full ? '#fff' : undefined, minHeight: full ? '100vh' : undefined, overflow: full ? 'auto' : undefined }}>
      {!full && (
        <PageHeader
          title={layout.name ? `Planta ${layout.name}` : 'Planta'}
          description="Tocá un sector del plano para ver su stock, la máquina, las matrices o la balanza. Los colores avisan qué mirar."
          actions={<>
            <LiveChip updatedAt={updatedAt} loading={loading} />
            <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={reload} sx={{ textTransform: 'none' }}>Actualizar</Button>
            <Button variant="outlined" size="small" startIcon={<Fullscreen />} onClick={toggleFull} sx={{ textTransform: 'none', display: { xs: 'none', md: 'inline-flex' } }}>Pantalla completa</Button>
          </>}
        />
      )}
      {full && (
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', flex: 1 }}>Planta {layout.name} · en vivo</Typography>
          <LiveChip updatedAt={updatedAt} loading={loading} />
          <Button variant="outlined" size="small" startIcon={<FullscreenExit />} onClick={toggleFull} sx={{ textTransform: 'none' }}>Salir</Button>
        </Stack>
      )}

      {failed > 0 && !loading && (
        <Alert severity="warning" sx={{ mb: 2 }}>Hay {failed} dato{failed > 1 ? 's' : ''} que no se pudo traer; esos sectores pueden verse vacíos. Se reintenta solo.</Alert>
      )}

      <KpiStrip>
        <KpiItem label="Producido hoy" value={<AnimatedNumber value={k?.goodQuantity ?? 0} />}
          hint={k ? `rotos ${fmtPct(k.scrapPct, 1)}` : ' '} tone={k && k.goodQuantity ? 'ok' : undefined} />
        <KpiItem label="Eficiencia hoy" value={k?.efficiencyPct != null ? <AnimatedNumber value={k.efficiencyPct * 100} suffix=" %" /> : '—'}
          hint="contra el estándar" tone={k?.efficiencyPct != null ? (k.efficiencyPct >= 0.85 ? 'ok' : k.efficiencyPct >= 0.6 ? 'warn' : 'bad') : undefined} />
        <KpiItem label="Máquinas con parte" value={<><AnimatedNumber value={machinesRunning} /><Box component="span" sx={{ color: MUTED, fontWeight: 600 }}> / {machineZones.length}</Box></>} hint="hoy" />
        <KpiItem label="Productos bajos" value={<AnimatedNumber value={red + yellow} />} hint={red ? `${red} sin stock` : yellow ? 'cerca del mínimo' : 'todo en verde'}
          tone={red ? 'bad' : yellow ? 'warn' : 'ok'} />
        <KpiItem label="Mantenimiento" value={<AnimatedNumber value={overdue} />} hint={overdue ? 'tareas vencidas' : soon ? `${soon} próximas` : 'al día'}
          tone={overdue ? 'bad' : soon ? 'warn' : 'ok'} />
        <KpiItem label="Materia prima" value={<AnimatedNumber value={mpKg} suffix=" kg" />} hint={`virgen ${new Intl.NumberFormat('es-AR').format(Math.round(virgin))} kg`} />
      </KpiStrip>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(440px, 560px)' }, gap: 2.5, alignItems: 'start' }}>
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2.5 }, bgcolor: SURFACE, borderRadius: 3 }}>
          <PlantMap layout={layout} statuses={statuses} selected={selected} onSelect={select} maxHeight={mapHeight} />
        </Paper>

        <Paper
          ref={panelRef}
          variant="outlined"
          sx={{
            borderRadius: 3, overflow: 'hidden', position: { lg: 'sticky' }, top: { lg: 16 }, scrollMarginTop: 80,
            height: { lg: full ? 'calc(100vh - 150px)' : 'max(600px, calc(100vh - 250px))' }, display: 'flex', flexDirection: 'column',
          }}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={zone?.id ?? 'summary'}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
              style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
            >
              {!zone && <SummaryPanel layout={layout} data={data} statuses={statuses} onSelect={select} updatedAt={updatedAt} />}
              {zone?.kind === 'scale' && <ScalePanel data={data} onSaved={reload} onClose={() => select(null)} />}
              {zone && Panel && (
                <Panel zone={zone} data={data} status={statuses[zone.id]} onClose={() => select(null)} onSelect={select} onSaved={reload} />
              )}
            </motion.div>
          </AnimatePresence>
        </Paper>
      </Box>
    </Box>
  );
};

const LiveChip: React.FC<{ updatedAt: Date | null; loading: boolean }> = ({ updatedAt, loading }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.5, py: 0.6, borderRadius: 999, bgcolor: '#E8F5EE', color: '#0F6B45', whiteSpace: 'nowrap' }}>
    <PulseDot tone="ok" size={8} pulse={!loading} />
    <Typography sx={{ fontSize: 13, fontWeight: 650 }}>
      {loading ? 'Conectando…' : `En vivo · ${updatedAt?.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) ?? ''}`}
    </Typography>
  </Stack>
);

export default FactoryPlantPage;
